import React, { useEffect, useState } from 'react';
import { api, isSpendApprovalError } from './api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sparkles, Copy, Instagram, Facebook, Linkedin, Music, Mail, MessageSquare, FileText, Newspaper, Loader2, BarChart3, Send, ShieldOff } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { Link } from 'react-router-dom';
import { copyText } from './clipboard';
import { formatDateTime } from './kit/time';
import SimpleMarkdown from './SimpleMarkdown';
import StudioSendModal from './StudioSendModal';
import ScheduledEmails from './ScheduledEmails';
import SuppressionDialog from './inbox/SuppressionDialog';

// Flyer and blog copy comes back as Markdown; the other formats are plain text.
const MARKDOWN_TYPES = ['flyer', 'blog'];
// Only these two can be sent from the Studio; the rest are copied to a social post or a page.
const SENDABLE_TYPES = ['email', 'sms'];

const types = [
  { k: 'instagram', l: 'Instagram', icon: Instagram, c: '#E1306C' },
  { k: 'facebook', l: 'Facebook', icon: Facebook, c: '#1877F2' },
  { k: 'linkedin', l: 'LinkedIn', icon: Linkedin, c: '#0A66C2' },
  { k: 'tiktok', l: 'TikTok', icon: Music, c: '#000' },
  { k: 'email', l: 'Email Campaign', icon: Mail, c: '#8DC63F' },
  { k: 'sms', l: 'SMS', icon: MessageSquare, c: '#6BA82E' },
  { k: 'flyer', l: 'Flyer', icon: FileText, c: '#f59e0b' },
  { k: 'blog', l: 'Blog', icon: Newspaper, c: '#8b5cf6' },
];

export default function MarketingPage() {
  const [optOutsOpen, setOptOutsOpen] = useState(false);
  const [type, setType] = useState('instagram');
  const [topic, setTopic] = useState('');
  const [loanProgram, setLoanProgram] = useState('');
  const [tone, setTone] = useState('professional');
  const [audience, setAudience] = useState('first-time homebuyers');
  const [length, setLength] = useState('medium');
  const [busy, setBusy] = useState(false);
  const [output, setOutput] = useState('');
  const [outputType, setOutputType] = useState('instagram');
  const [history, setHistory] = useState([]);
  const [sending, setSending] = useState(false);
  const [sentTick, setSentTick] = useState(0);

  const loadHist = async () => { const { data } = await api.get('/marketing/history'); setHistory(data); };
  useEffect(() => { loadHist(); }, []);

  const generate = async () => {
    if (!topic.trim()) { toast({ title: 'Add a topic' }); return; }
    setBusy(true); setOutput('');
    try {
      const { data } = await api.post('/marketing/generate', { content_type: type, topic, loan_program: loanProgram, tone, audience, length });
      setOutput(data.output);
      setOutputType(type);
      loadHist();
    } catch (e) {
      // Ticket 167: the interceptor already toasted the approval message.
      if (!isSpendApprovalError(e)) toast({ title: 'Failed', description: e.response?.data?.detail || 'AI error' });
    }
    setBusy(false);
  };

  // Email copy from before the licence footer existed (history) has no Equal Housing line; do not send it.
  const canSend = SENDABLE_TYPES.includes(outputType) && (outputType === 'sms' || /Equal Housing/i.test(output));

  const copyOut = async () => {
    if (await copyText(output)) toast({ title: 'Copied to clipboard' });
    else toast({ title: 'Copy failed', description: 'Select the text and copy it manually' });
  };

  return (
    <div className="p-4 md:p-8">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl font-bold flex items-center gap-2" style={{ color: '#070B5F' }}>
            Marketing Studio <Sparkles size={22} style={{ color: '#6BA82E' }} />
          </h1>
          <p className="text-slate-500 mt-1">Generate on-brand, compliant mortgage marketing in seconds.</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setOptOutsOpen(true)}
            data-testid="studio-opt-outs"
            className="inline-flex items-center gap-1 text-[12px] font-semibold text-slate-600 hover:text-slate-900"
          >
            <ShieldOff size={13} /> Opt-outs
          </button>
          <Link
            to="/portal/email-analytics"
            data-testid="open-email-analytics"
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-2 rounded-lg text-white shadow-sm hover:brightness-110"
            style={{ background: 'linear-gradient(135deg, #070B5F 0%, #0f1580 100%)' }}
          >
            <BarChart3 size={13} /> Email Analytics
          </Link>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Inputs */}
        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <Label className="text-sm font-semibold" style={{ color: '#070B5F' }}>Format</Label>
          <div className="grid grid-cols-4 gap-2 mt-2 mb-5">
            {types.map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.k} onClick={() => setType(t.k)} className={`p-3 rounded-xl flex flex-col items-center gap-1 transition ${type === t.k ? 'border-2' : 'border border-slate-200 hover:border-slate-400'}`} style={type === t.k ? { borderColor: t.c, background: t.c + '10' } : {}}>
                  <Icon size={18} style={{ color: t.c }} />
                  <span className="text-[11px] font-semibold" style={{ color: '#070B5F' }}>{t.l}</span>
                </button>
              );
            })}
          </div>

          <div className="space-y-4">
            <div><Label>Topic *</Label><Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. 'Why now is a great time to refinance'" className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Loan Program</Label>
                <Select value={loanProgram} onValueChange={setLoanProgram}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Any" /></SelectTrigger>
                  <SelectContent>{['FHA','VA','USDA','Conventional','Jumbo','Refinance','HELOC','DSCR','Bank Statement'].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Tone</Label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="professional">Professional</SelectItem><SelectItem value="casual">Casual</SelectItem><SelectItem value="educational">Educational</SelectItem><SelectItem value="promotional">Promotional</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Audience</Label><Input value={audience} onChange={(e) => setAudience(e.target.value)} className="mt-1" /></div>
            <div><Label>Length</Label>
              <div className="flex gap-2 mt-1">
                {['short','medium','long'].map(l => <button key={l} onClick={() => setLength(l)} className={`flex-1 py-2 rounded-lg text-sm font-semibold capitalize ${length === l ? 'text-white' : 'bg-slate-100 text-slate-600'}`} style={length === l ? { background: '#070B5F' } : {}}>{l}</button>)}
              </div>
            </div>

            <button onClick={generate} disabled={busy} className="btn-gold w-full justify-center">
              {busy ? <><Loader2 size={16} className="animate-spin" /> Generating...</> : <><Sparkles size={16} /> Generate with AI</>}
            </button>
          </div>
        </div>

        {/* Output */}
        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="font-serif font-semibold" style={{ color: '#070B5F' }}>Output</div>
            {output && (
              <div className="flex items-center gap-4">
                {canSend && (
                  <button onClick={() => setSending(true)} data-testid="studio-send" className="flex items-center gap-1.5 text-sm font-semibold hover:opacity-70" style={{ color: '#070B5F' }}><Send size={14} /> Send…</button>
                )}
                <button onClick={copyOut} className="flex items-center gap-1.5 text-sm font-semibold hover:opacity-70" style={{ color: '#6BA82E' }}><Copy size={14} /> Copy</button>
              </div>
            )}
          </div>
          {!output && !busy && <div className="text-center text-slate-400 py-16"><Sparkles size={28} className="mx-auto mb-2 opacity-40" /><p className="text-sm">Your AI-generated copy will appear here.</p></div>}
          {busy && <div className="text-center py-16"><Loader2 size={28} className="mx-auto animate-spin" style={{ color: '#6BA82E' }} /><p className="text-sm text-slate-500 mt-3">Audrey is writing...</p></div>}
          {output && (
            <div className={`${MARKDOWN_TYPES.includes(outputType) ? '' : 'whitespace-pre-wrap '}text-sm leading-relaxed p-4 bg-slate-50 rounded-xl`} style={{ color: '#0f172a' }} data-testid="studio-output">
              {MARKDOWN_TYPES.includes(outputType) ? <SimpleMarkdown text={output} /> : output}
            </div>
          )}
        </div>
      </div>

      <StudioSendModal open={sending} onClose={() => setSending(false)} onSent={() => setSentTick((n) => n + 1)} channel={outputType} output={output} />

      <ScheduledEmails refreshKey={sentTick} />

      {optOutsOpen && <SuppressionDialog open={optOutsOpen} onOpenChange={setOptOutsOpen} />}

      {history.length > 0 && (
        <div className="mt-8">
          <h2 className="font-serif text-xl font-semibold mb-3" style={{ color: '#070B5F' }}>Recent Generations</h2>
          <div className="space-y-2">
            {history.slice(0, 8).map((h) => (
              <button key={h.content_id} onClick={() => { setOutput(h.output); setOutputType(h.request.content_type); }} className="w-full text-left p-3 bg-white rounded-xl border border-slate-100 hover:border-slate-300 transition">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold capitalize" style={{ color: '#070B5F' }}>{h.request.content_type} — {h.request.topic}</div>
                  <div className="text-xs text-slate-400">{formatDateTime(h.created_at)}</div>
                </div>
                <div className="text-xs text-slate-500 mt-1 line-clamp-2">{h.output.slice(0, 200)}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
